import React from "react";
import { func, node, oneOf, oneOfType, string } from "prop-types";

import { PseudoButton } from "../../atoms";
import buttonThemes from "../../constants/buttonThemes";
import calloutCardThemes from "../../constants/calloutCardThemes";
import imagePositions from "../../constants/imagePositions";
import * as S from "../styled";

const HorizontalCalloutCardButton = React.forwardRef(
  (
    {
      buttonTheme,
      callToActionCopy,
      description,
      headline,
      illustration,
      imagePosition,
      onClick,
      theme,
      ...passThroughProps
    },
    ref
  ) => (
    <S.HorizontalCardWrapper
      ref={ref}
      as="button"
      interactive
      onClick={onClick}
      theme={theme}
      {...passThroughProps}
    >
      {illustration && (
        <S.IllustrationWrapper imagePosition={imagePosition}>
          {illustration}
        </S.IllustrationWrapper>
      )}
      <S.ContentWrapper>
        <S.Headline>{headline}</S.Headline>
        {description && <S.Description>{description}</S.Description>}
        <S.CallToAction
          as={PseudoButton}
          buttonTheme={buttonTheme}
          theme={theme}
        >
          {callToActionCopy}
        </S.CallToAction>
      </S.ContentWrapper>
    </S.HorizontalCardWrapper>
  )
);

HorizontalCalloutCardButton.propTypes = {
  /**
   * The theme for the pseudo button within the Callout Card. One of:
   *   - PRIMARY_BUTTON
   *   - SECONDARY_BUTTON
   */
  buttonTheme: oneOf(Object.values(buttonThemes)),
  /**
   * The text inside of the pseudo button within the Callout Card
   */
  callToActionCopy: oneOfType([node, string]).isRequired,
  /**
   * Adds a description block to the Callout Card
   */
  description: string,
  /**
   * Headline for the Callout Card
   */
  headline: string.isRequired,
  /**
   * A component that defines the illustration for the Callout Card.
   * Please use either:
   *   - Brand icon (Images and Icons/Icon/Brand) of size MD
   *   - Avatar of size SM
   */
  illustration: node,
  /**
   * If an illustration is provided, imagePosition will allow to justify it to the right.
   * It is one of:
   *   - LEFT (default)
   *   - RIGHT
   */
  imagePosition: oneOf([imagePositions.LEFT, imagePositions.RIGHT]),
  /**
   * Click callback handler when a user clicks the Callout Card
   * (the entire card, not just the button)
   */
  onClick: func.isRequired,
  /**
   * The Callout Card theme. One of:
   *   - BLUE
   *   - ORANGE
   *   - GRAY
   *   - WHITE
   */
  theme: oneOf(Object.values(calloutCardThemes))
};

HorizontalCalloutCardButton.defaultProps = {
  buttonTheme: buttonThemes.PRIMARY_BUTTON,
  description: null,
  illustration: null,
  imagePosition: imagePositions.LEFT,
  theme: calloutCardThemes.WHITE
};

export default HorizontalCalloutCardButton;
